import api from './api';
import { AuthResponse, User } from '@/types';

// Global logout callback that can be set by AuthContext
let globalLogoutCallback: (() => void) | null = null;

export function setGlobalLogoutCallback(callback: () => void) {
  globalLogoutCallback = callback;
}

export function triggerGlobalLogout() {
  if (globalLogoutCallback) {
    globalLogoutCallback();
  }
}

// Helper function to decode JWT token
function parseJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error parsing JWT:', e);
    return null;
  }
}

export const authService = {
  /** Invited staff: answer NEW_PASSWORD_REQUIRED with their chosen password. */
  async completeNewPassword(email: string, newPassword: string, session: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/respond-to-challenge', {
      email,
      newPassword,
      session,
    });
    const data = response.data;
    if (data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('idToken', data.idToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    }
    return data;
  },

  async forgotPassword(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email });
  },

  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    await api.post('/auth/confirm-forgot-password', {
      email,
      code,
      newPassword,
    });
  },

  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/auth/employee/signin', {
        email,
        password,
      });


      const data = response.data;

      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('idToken', data.idToken);
        localStorage.setItem('refreshToken', data.refreshToken);

        // If userInfo is missing, try to get it from the ID token
        if (!data.userInfo && data.idToken) {
          const decodedToken = parseJwt(data.idToken);

          // Construct userInfo from token claims
          const userInfo: User = {
            email: decodedToken.email || decodedToken['cognito:username'],
            firstName: decodedToken.given_name || decodedToken.name?.split(' ')[0] || '',
            lastName: decodedToken.family_name || decodedToken.name?.split(' ')[1] || '',
            roles: ['pharmacist'], // TEMPORARY: Hardcode for now since we know the user has this role
            userType: 'employee',
          };

          localStorage.setItem('userInfo', JSON.stringify(userInfo));
          data.userInfo = userInfo;
        } else if (data.userInfo) {
          localStorage.setItem('userInfo', JSON.stringify(data.userInfo));
        }
      }

      return data;
    } catch (error) {
      console.error('Login service error:', error);
      throw error;
    }
  },

  logout(): void {
    if (typeof window !== 'undefined') {
      localStorage.clear();
    }
    // Trigger global logout callback to update React state
    if (globalLogoutCallback) {
      globalLogoutCallback();
    }
  },

  getCurrentUser(): User | null {
    if (typeof window === 'undefined') return null;
    
    const userInfo = localStorage.getItem('userInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  },

  isAuthenticated(): boolean {
    if (typeof window === 'undefined') return false;
    
    return !!localStorage.getItem('accessToken');
  },

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.roles?.includes(role) || false;
  },

  async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return false;
      }

      // Use direct axios call instead of api instance to bypass interceptors
      const API_BASE_URL = import.meta.env.VITE_API_URL;
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        console.error('Token refresh failed with status:', response.status);
        return false;
      }

      const data: AuthResponse = await response.json();

      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('idToken', data.idToken);
        
        // Store the new refresh token if provided (token rotation)
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  },
};